import Vue from 'vue';
import {
  getNestedStateObject
} from '@/utils';

const runValidation = (fieldData, data) => {
  // reset our custom validation each time
  if (data.target.validationMessage) {
    data.target.setCustomValidity('');
  }

  // check initial validity
  let valid = false;

  // if its a radio button, check at least one is valid
  if (Array.isArray(data.target)) {
    valid = data.target.some(target => target.checkValidity());
  } else {
    valid = data.target.checkValidity();
  }

  // run customValidation function if needed
  if (valid && fieldData.customValidation) {
    return fieldData.customValidation(data.target);
  }

  return {
    valid,
    message: data.target.value ? data.target.title : ''
  };
}

export default {
  /**
   * setup a form field (and the main form object if needed)
   * sets nested state data to ensure things don't break
   */
  setupField(state, data) {
    // create nested objects (where necessary) until we reach the desired place
    let formData = state;
    for (let i = 0; i < data.vuexPath.length; i++) {
      if (typeof formData[data.vuexPath[i]] === 'undefined' || formData[data.vuexPath[i]] === null) {
        Vue.set(formData, data.vuexPath[i], {});
      }
      formData = formData[data.vuexPath[i]];
    }

    // set the form state and the field to the fields object
    Vue.set(formData, 'summaryView', !!formData.summaryView);
    Vue.set(formData, 'hasBeenSubmitted', !!formData.hasBeenSubmitted);
    Vue.set(formData, 'fields', {
      ...formData.fields,
      [data.name]: {
        value: data.value,
        error: data.required,
        message: '',
        customValidation: data.customValidation,
        userHasTyped: false
      }
    });
  },

  destroyField(state, data) {
    const formData = getNestedStateObject(state, [...data.vuexPath, 'fields']);
    if (formData) {
      Vue.delete(formData, data.name);

      // if we have deleted all fields
      // reset the main form hasBeenSubmitted to false
      if (!Object.keys(formData).length) {
        Vue.set(getNestedStateObject(state, [...data.vuexPath]), 'hasBeenSubmitted', false);
      }
    }
  },

  /** set a single field value */
  setFieldValue(state, data) {
    Vue.set(getNestedStateObject(state, [...data.vuexPath, 'fields', data.name]), 'value', data.value);
  },

  /** set field values from a flat object (also handles nesting) */
  setFieldValuesFromFlat(state, data) {
    const formData = getNestedStateObject(state, data.vuexPath);

    const setFieldValuesFromFlat = (object, data) => {
      for (const fieldName in data) {
        if (fieldName === 'fields') {
          continue;
        }

        if (typeof data[fieldName] === 'object' && data[fieldName] !== null) {
          if (object.fields[fieldName]) {
            setFieldValuesFromFlat(object.fields[fieldName], data[fieldName]);
          } else if (data[fieldName].fields) {
            setFieldValuesFromFlat(data[fieldName], data[fieldName]);
          }
        } else if (object.fields[fieldName]) {
          Vue.set(object.fields[fieldName], 'value', data[fieldName]);
        }
      }
    }

    setFieldValuesFromFlat(formData, data.flat);
  },

  /** set error status on field */
  setFieldError(state, data) {
    const fieldData = getNestedStateObject(state, [...data.vuexPath, 'fields', data.name]);
    const validationStatus = runValidation(fieldData, data);

    Vue.set(getNestedStateObject(state, [...data.vuexPath, 'fields']), data.name, {
      ...fieldData,
      error: !validationStatus.valid,
      message: validationStatus.message
    });
  },

  /** validate a single field */
  validateField(state, data) {
    const fieldData = getNestedStateObject(state, [...data.vuexPath, 'fields', data.name]);
    const validationStatus = runValidation(fieldData, data);

    Vue.set(getNestedStateObject(state, [...data.vuexPath, 'fields']), data.name, {
      ...fieldData,
      error: !validationStatus.valid,
      message: validationStatus.message,
      userHasTyped: true
    });
  },

  /** set the has been submitted boolean */
  setHasBeenSubmitted(state, vuexPath) {
    const formData = getNestedStateObject(state, vuexPath);
    Vue.set(formData, 'hasBeenSubmitted', true);

    // submit address if it exists
    if (formData.address && formData.address.fields) {
      Vue.set(formData.address, 'hasBeenSubmitted', true);
      Vue.set(formData.address.fields.addressLines, 'hasBeenSubmitted', true);
    }

    // submit visa if it exists
    if (formData.visaIds && formData.visaIds.fields) {
      Vue.set(formData.visaIds, 'hasBeenSubmitted', true);
    }
  },

  /** set the has been submitted boolean */
  unsetHasBeenSubmitted(state, vuexPath) {
    const formData = getNestedStateObject(state, vuexPath);
    Vue.set(formData, 'hasBeenSubmitted', false);

    // unsubmit address if it exists
    if (formData.address && formData.address.fields) {
      Vue.set(formData.address, 'hasBeenSubmitted', false);
      Vue.set(formData.address.fields.addressLines, 'hasBeenSubmitted', false);
    }

    // unsubmit visa if it exists
    if (formData.visaIds && formData.visaIds.fields) {
      Vue.set(formData.visaIds, 'hasBeenSubmitted', false);
    }
  },

  /** set summary view boolean */
  setSummaryView(state, vuexPath) {
    Vue.set(getNestedStateObject(state, vuexPath), 'summaryView', true);
  },


  /** unset summary view boolean */
  unsetSummaryView(state, vuexPath) {
    Vue.set(getNestedStateObject(state, vuexPath), 'summaryView', false);
  }
}